import { useRef, useCallback, useState, useEffect } from 'react';
import { Alert, Box } from '@mui/material';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  ButtonContainer,
  IconLibrary,
  PermissionButton,
} from '@product-framework/index';
import { FormattedMessage } from 'react-intl';
import schema from './edit.json';
import validationSchema from './validations';
import {
  fetchLocation,
  fetchContact,
  fetchAddress,
  destructLocation,
  editLocation,
  editSPContact,
  editSPAddress,
  changeLocationStatus,
  getStatusButton,
  postProcessedAddressApiData,
} from './lib';

const FORM_ID = 'EditServiceProviderNewLocationForm';
const PERMISSION_SP = 'SERVICEPROVIDERLOCATION';

function ServiceProviderLocationEdit({
  serviceProviderId,
  locationId: pageId,
  tabErrors,
  onChange = () => undefined,
}) {
  const formRef = useRef();
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, get, post, getApiUrl } = useDataProvider(provider);

  const commonParams = {
    put,
    get,
    post,
    getApiUrl,
    serviceProviderId,
    pageId,
    formatMessage,
  };

  const [statusButton, setStatusButton] = useState({
    Icon: null,
    text: null,
  });
  const [currentStatus, setCurrentStatus] = useState(null);

  const fetchDefaultValues = useCallback(async () => {
    let pageData = {};
    const [responseFetchLocation, responseFetchContact, responseFetchAddress] =
      await Promise.all([
        fetchLocation(commonParams),
        fetchContact(commonParams),
        fetchAddress(commonParams),
      ]);

    if (responseFetchLocation) {
      pageData = {
        ...pageData,
        ...destructLocation(responseFetchLocation),
      };
    }

    if (responseFetchAddress && responseFetchAddress.data.length > 0) {
      const addressData = responseFetchAddress?.data?.[0];
      pageData = {
        ...pageData,
        ...postProcessedAddressApiData(addressData),
        fullAddressData: addressData,
      };
    }

    if (responseFetchContact && responseFetchContact.data.length > 0) {
      pageData = {
        ...pageData,
        mainContact: responseFetchContact.data[0],
      };
    }

    setCurrentStatus(responseFetchLocation?.status);

    const result = {
      data: {
        ...pageData,
        serviceProviderId,
      },
      errors: null,
    };
    return result;
  }, [serviceProviderId]);

  const onSubmit = async data => {
    const [spLocationResponse, spContactEditResponse, spAddressEditResponse] =
      await Promise.all([
        editLocation(commonParams, data),
        editSPContact(commonParams, data),
        editSPAddress(commonParams, data, formRef),
      ]);
    if (
      spLocationResponse?.ok &&
      spAddressEditResponse?.ok &&
      spContactEditResponse?.ok
    ) {
      const locationDetails = destructLocation(spLocationResponse?.data);

      const addressData = spAddressEditResponse.data;
      const addressDetails = postProcessedAddressApiData(addressData);

      const updatedPageData = {
        ...locationDetails,
        ...addressDetails,
        mainContact: spContactEditResponse?.data,
        fullAddressData: addressData,
      };

      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEPROVIDER_EDIT_SUCCESS',
          defaultMessage: 'ServiceProvider was saved successfully',
        }),
      );

      setCurrentStatus(locationDetails?.status);
      return {
        nextValues: updatedPageData,
        errors: null,
        callback: () => {
          onChange();
        },
      };
    }
    let errors = [];
    if (spLocationResponse?.errors) {
      errors = [...spLocationResponse.errors];
    }
    if (spAddressEditResponse?.errors) {
      errors = [...errors, ...spAddressEditResponse.errors];
    }
    if (spContactEditResponse?.errors) {
      errors = [...errors, ...spContactEditResponse.errors];
    }
    return {
      nextValues: data,
      errors,
    };
  };

  const changeServiceProviderLocationStatus = async () => {
    const defaultValues = formRef.current.getDefaultValues();
    const unSavedDialog = formRef.current.getActionDialog();
    const isCurrentStatusActive = defaultValues.status === 'ACTIVE';
    const data = { status: isCurrentStatusActive ? 'INACTIVE' : 'ACTIVE' };
    const response = await changeLocationStatus(commonParams, data);
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEPROVIDER_EDIT_LOCATION_STATUS_SUCCESS',
          defaultMessage: `Location status was updated to ${isCurrentStatusActive ? 'In-Active' : 'Active'}`,
        }),
      );
      setCurrentStatus(response?.data?.status);
      return {
        nextValues: {
          ...defaultValues,
          ...response?.data,
          serviceProviderId,
        },
        errors: null,
        callback: () => {
          onChange();
          unSavedDialog.close();
        },
      };
    }
    let errors = [];

    if (response?.errors) {
      errors = [...response.errors];
    }

    return {
      nextValues: null,
      errors,
      callback: () => {
        unSavedDialog.close();
      },
    };
  };

  const showStatusChangeConfirmationDialog = () => {
    const defaultValues = formRef.current.getDefaultValues();
    const currentStatusIsActive =
      formRef?.current?.getDefaultValues()?.status === 'ACTIVE';
    const unSavedDialog = formRef.current.getActionDialog();
    unSavedDialog.init({
      title: 'Change Status',
      message: `Do you want the set the status of ${defaultValues.locationName} to ${currentStatusIsActive ? 'In-Active' : 'Active'}?`,
      width: 360,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            formRef?.current?.onFormButtonAction(FORM_ID, () =>
              changeServiceProviderLocationStatus(),
            );
          },
        },
        {
          label: 'No',
          onClick: () => {
            unSavedDialog.close();
          },
          variant: 'outlined',
        },
      ],
    });
    unSavedDialog.open();
  };

  const showUnSavedChangesDialog = () => {
    const unSavedDialog = formRef.current.getActionDialog();
    unSavedDialog.init({
      message: `You have unsaved changes on the form. Do you want to save the changes before proceeding?`,
      width: 360,
      size: 'small',
      buttons: [
        {
          label: 'Save and Proceed',
          onClick: () => {
            formRef?.current?.submit({
              formId: FORM_ID,
              onSuccess: () =>
                formRef?.current?.onFormButtonAction(FORM_ID, () =>
                  changeServiceProviderLocationStatus(),
                ),
              onFailure: () => {
                unSavedDialog.close();
              },
            });
          },
        },
        {
          label: 'Discard and Proceed',
          onClick: () => {
            formRef?.current?.reset();
            formRef?.current?.onFormButtonAction(FORM_ID, () =>
              changeServiceProviderLocationStatus(),
            );
          },
        },
        {
          label: 'Close',
          onClick: () => {
            unSavedDialog.close();
          },
          variant: 'outlined',
        },
      ],
    });
    unSavedDialog.open();
  };

  const toggleServiceProviderLocationStatus = async () => {
    if (formRef.current?.isDirty()) {
      showUnSavedChangesDialog();
    } else {
      showStatusChangeConfirmationDialog();
    }
  };

  useEffect(() => {
    setStatusButton(getStatusButton(commonParams, currentStatus, IconLibrary));
  }, [currentStatus]);

  const hasLocationError =
    Array.isArray(tabErrors?.hasInvalidLocationAddress) &&
    tabErrors.hasInvalidLocationAddress.find(
      item => item.locationId === pageId,
    );

  return (
    <Box sx={{ pt: 3 }}>
      {hasLocationError && (
        <Box sx={{ mb: 1 }}>
          <Alert severity="error">
            <FormattedMessage
              id="LOCATION_ERROR"
              defaultMessage="We couldn't verify this address. Please double-check for accuracy. An invalid address will prevent you from getting Jobs."
            />
          </Alert>
        </Box>
      )}
      <Form
        formId={FORM_ID}
        ref={formRef}
        permissionEntities={[PERMISSION_SP]}
        onSubmit={onSubmit}
        schema={schema}
        fetchDefaultValues={fetchDefaultValues}
        validationSchema={validationSchema(formatMessage)}>
        <ButtonContainer>
          <PermissionButton
            variant="contained"
            permissionName="CAN_UPDATE_SP_LOCATIONS"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'SERVICEPROVIDER_EDIT_SAVE_BUTTON',
              defaultMessage: 'Save Service Provider',
            })}
          </PermissionButton>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_LOCATIONS"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Reset />}
            onClick={() => formRef.current?.reset()}>
            {formatMessage({
              id: 'SERVICEPROVIDER_EDIT_RESET_BUTTON',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
          {statusButton?.Icon && (
            <PermissionButton
              variant="contained"
              color="primary"
              permissionName="CAN_UPDATE_SP_LOCATIONS"
              startIcon={<statusButton.Icon />}
              onClick={() => toggleServiceProviderLocationStatus()}>
              {statusButton?.text}
            </PermissionButton>
          )}
        </ButtonContainer>
      </Form>
    </Box>
  );
}

export default observer(ServiceProviderLocationEdit);
